import { useState } from "react";
import { useMutation } from "@apollo/client";
import { PackageCrudResult } from "@models/ISchema";
import packagesCrud from "../graphql/packagesCrud.mutation.graphql";
import { DataReducerType } from "../context/types";

interface Props {
  origin: string;
  scenario: string;
}

type ReturnType = [
  { result: any; error: boolean; isLoading: boolean },
  (data: DataReducerType, options?: PackageCrudOptions) => Promise<void>,
];

interface PackageCrudOptions {
  dryRun?: boolean;
  skipValidations?: boolean;
}

const usePackagesCrud = (props: Props): ReturnType => {
  const { scenario, origin } = props;
  const [packagesCrudMutation] = useMutation(packagesCrud, {});
  const [result, setResult] = useState<PackageCrudResult>({});
  const [error, setError] = useState(false);
  const [isLoading, setIsloading] = useState(false);

  /**
   * @param creations - LinkPopulationObj
   * @return LinkPopulationPair
   */
  const buildCreations = (creations: DataReducerType["creations"]) =>
    Object.values(creations ?? {}).map(creation => ({
      linkId: creation?.linkId,
      termId: creation?.termId,
      populationCode: creation?.population?.code,
      packageName: creation?.packageName,
    }));

  const setInput = ({ creations }: DataReducerType, dryRun: boolean, skipValidations: boolean) => ({
    dryRun,
    skipValidations,
    changesets: {
      creates: buildCreations(creations),
      deletes: [],
    },
  });

  const submitPackagesCrud = async (
    data: DataReducerType,
    { dryRun = true, skipValidations = true }: PackageCrudOptions = {},
  ) => {
    setIsloading(true);

    try {
      const variables = {
        scenarioId: scenario,
        originId: origin,
        input: setInput(data, dryRun, skipValidations),
      };
      const { data: responseData } = await packagesCrudMutation({ variables });
      setResult(responseData?.cube?.packagesCrud);
    } catch (e) {
      setError(true);
      console.error("[PackagesCrud]: ", e);
    } finally {
      setIsloading(false);
    }
  };

  return [{ result, error, isLoading }, submitPackagesCrud];
};

export default usePackagesCrud;
